import { useState } from "react";
import Logo from "@/components/tpr/Logo/Logo";
import RandomWallpaper from "@/components/tpr/RandomWallpaper/RandomWallpaper";
import TprIcon from "@/components/tpr/TprIcon/TprIcon";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Item, ItemContent } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { linkClasses } from "@/constants/ui";
import { useResetPassword } from "@/hooks/useResetPassword";
import { Link } from "react-router";

const ForgottenPasswordView = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { mutate: requestReset, isPending, error } = useResetPassword();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) return;

    requestReset(
      {
        email: email.trim(),
        redirectTo: `${window.location.origin}/reset-password`,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            setSubmitted(true);
          }
        },
      },
    );
  };

  const renderSuccessMessage = () => {
    return (
      <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
        <ItemContent>
          <div className="flex flex-col gap-4">
            <div
              className="p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg dark:bg-green-200 dark:text-green-800"
              role="alert"
            >
              <p className="font-semibold">Check your email</p>
              <p className="mt-2">
                If an account exists with {email}, you will receive an email with instructions on how to reset your
                password.
              </p>
            </div>
            <Link to="/login" className={linkClasses}>
              Return to login
            </Link>
          </div>
        </ItemContent>
      </Item>
    );
  };

  const renderForm = () => {
    return (
      <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
        <ItemContent>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-2">
              {error && (
                <div
                  className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                  role="alert"
                >
                  An error occurred. Please try again.
                </div>
              )}
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="email">Email address</FieldLabel>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      className="bg-white dark:text-gray-900 text-lg"
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                      required
                    />
                  </Field>
                  <Field orientation="horizontal">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!email || isPending}
                      aria-label="Send reset email"
                    >
                      {isPending ? <Spinner className="h-4 w-4 mr-2" /> : null}
                      Send reset link
                    </Button>
                  </Field>
                </FieldGroup>
              </FieldSet>
              <div className="mt-4">
                <Link to="/login" className={linkClasses}>
                  Back to login
                </Link>
              </div>
            </div>
          </form>
        </ItemContent>
      </Item>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="flex justify-center h-screen">
        <RandomWallpaper />

        <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-3/6">
          <div className="flex-1">
            <div className="text-center flex flex-col justify-center items-center">
              <div className="mb-4">
                <TprIcon size="large" />
              </div>
              <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                <Logo color="black" size="large" />
              </h2>
              <p className="mt-3 text-gray-500 dark:text-gray-300">Reset your password</p>
            </div>

            <div className="mt-8 w-full">{submitted ? renderSuccessMessage() : renderForm()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgottenPasswordView;
